import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { PlanStep, StepStatus, Participant, PlanRole } from '../../types/generated/graphql';
import { TaskTree, findGlobalNextUp } from '../../utils/taskTree';
import { colors } from '../../theme/colors';
import { useAuth } from '../../contexts/AuthContext';
import { getUnassignedRequiredRoles, resolveStepAssignee, resolveCurrentParticipantId } from '../../utils/planHelpers';
import GravatarAvatar from '../GravatarAvatar';
import ParticipantsPanel from '../ParticipantsPanel';

interface OverviewPlaybookSpaceProps {
  planId: string;
  taskTree: TaskTree;
  steps: PlanStep[];
  participants: Participant[];
  roles: PlanRole[];
  onNavigateToTask: (taskId: string) => void;
  onAssignRole?: (roleId: string) => void;
}

export const OverviewPlaybookSpace: React.FC<OverviewPlaybookSpaceProps> = ({
  planId,
  taskTree,
  steps,
  participants,
  roles,
  onNavigateToTask,
  onAssignRole,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.uid || null;

  // Find current user's participant ID (with email fallback for unlinked participants)
  const currentParticipantId = useMemo(() => {
    return resolveCurrentParticipantId(currentUserId, user?.email, participants);
  }, [currentUserId, user?.email, participants]);

  // Get unassigned required roles
  const unassignedRequiredRoles = useMemo(() => {
    return getUnassignedRequiredRoles(roles);
  }, [roles]);

  // Find recommended next task - using role-based resolution
  const recommendedNext = useMemo(() => {
    const globalNext = findGlobalNextUp(taskTree);
    if (!globalNext) return null;

    // Resolve assignee using role-based logic
    const resolved = resolveStepAssignee(globalNext, roles, participants);

    // If task is unassigned or assigned to me, recommend it
    if (!resolved.participant || resolved.participant.id === currentParticipantId) {
      return globalNext;
    }

    // Otherwise, find the first task that's mine or unassigned
    for (const step of steps) {
      if (step.status === StepStatus.Completed || step.status === StepStatus.Skipped) continue;
      const stepResolved = resolveStepAssignee(step, roles, participants);
      if (!stepResolved.participant || stepResolved.participant.id === currentParticipantId) {
        return step;
      }
    }

    return null;
  }, [taskTree, steps, roles, participants, currentParticipantId]);

  // Get owner info for recommended next (using role resolution)
  const recommendedNextOwner = useMemo(() => {
    if (!recommendedNext) return null;
    const resolved = resolveStepAssignee(recommendedNext, roles, participants);
    return resolved.participant;
  }, [recommendedNext, roles, participants]);

  // Find tasks I'm waiting on (assigned to others via role, not completed)
  const waitingOnOthers = useMemo(() => {
    if (!currentParticipantId) return [];

    return steps.filter((step) => {
      if (step.status === StepStatus.Completed || step.status === StepStatus.Skipped) return false;
      const resolved = resolveStepAssignee(step, roles, participants);
      // Task is assigned to someone else (not me, and not unassigned)
      return resolved.participant && resolved.participant.id !== currentParticipantId;
    }).slice(0, 3); // Show max 3
  }, [steps, roles, participants, currentParticipantId]);

  // Check if all remaining tasks are delegated
  const allTasksDelegated = useMemo(() => {
    if (!currentParticipantId) return false;

    const activeTasks = steps.filter(
      (s) => s.status !== StepStatus.Completed && s.status !== StepStatus.Skipped
    );

    if (activeTasks.length === 0) return false;

    return activeTasks.every((s) => {
      const resolved = resolveStepAssignee(s, roles, participants);
      return resolved.participant && resolved.participant.id !== currentParticipantId;
    });
  }, [steps, roles, participants, currentParticipantId]);

  const handleNextUpClick = () => {
    if (recommendedNext) {
      onNavigateToTask(recommendedNext.id);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
      {/* Main content column */}
      <Box sx={{ flex: { xs: 1, md: 2 }, minWidth: 0 }}>
        {/* Warning banner for unassigned required roles */}
        {unassignedRequiredRoles.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon fontSize="small" />}
            sx={{
              mb: 3,
              bgcolor: colors.amber[50],
              border: `1px solid ${colors.amber[200]}`,
              '& .MuiAlert-icon': { color: colors.amber[600] },
              '& .MuiAlert-message': { color: colors.textPrimary },
            }}
            action={
              onAssignRole && unassignedRequiredRoles.length === 1 ? (
                <Button
                  size="small"
                  onClick={() => onAssignRole(unassignedRequiredRoles[0].roleId)}
                  sx={{ textTransform: 'none', color: colors.amber[700] }}
                >
                  Assign
                </Button>
              ) : undefined
            }
          >
            {unassignedRequiredRoles.length === 1 ? (
              <>
                This plan requires a <strong>{unassignedRequiredRoles[0].name}</strong>.
                Assign someone to continue.
              </>
            ) : (
              <>
                This plan requires: <strong>{unassignedRequiredRoles.map(r => r.name).join(', ')}</strong>.
                Assign people to these roles to continue.
              </>
            )}
          </Alert>
        )}

        {/* Recommended Next Card */}
        {recommendedNext ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: colors.blue[50],
              border: `1px solid ${colors.blue[200]}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <StarIcon sx={{ color: colors.amber[500], fontSize: 20 }} />
              <Typography
                variant="caption"
                sx={{
                  color: colors.blue[600],
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                Recommended Next
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body1"
                  sx={{ color: colors.textPrimary, fontWeight: 600, mb: 1 }}
                >
                  {recommendedNext.title}
                </Typography>
                {recommendedNextOwner && (
                  <Chip
                    size="small"
                    avatar={
                      <GravatarAvatar
                        email={recommendedNextOwner.email}
                        name={recommendedNextOwner.name}
                        size={20}
                        showTooltip={false}
                      />
                    }
                    label={recommendedNextOwner.name}
                    sx={{
                      bgcolor: 'white',
                      border: `1px solid ${colors.gray[200]}`,
                      '& .MuiChip-label': {
                        fontSize: '0.75rem',
                        color: colors.textSecondary,
                      },
                    }}
                  />
                )}
              </Box>
              <Button
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleNextUpClick}
                sx={{
                  textTransform: 'none',
                  bgcolor: colors.blue[600],
                  px: 3,
                  '&:hover': {
                    bgcolor: colors.blue[700],
                  },
                }}
              >
                Start
              </Button>
            </Box>
          </Paper>
        ) : allTasksDelegated ? (
          /* No actions for you - all delegated */
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              bgcolor: colors.gray[50],
              border: `1px solid ${colors.gray[200]}`,
              borderRadius: 2,
              textAlign: 'center',
            }}
          >
            <HourglassEmptyIcon sx={{ color: colors.gray[400], fontSize: 28, mb: 1 }} />
            <Typography
              variant="body2"
              sx={{ color: colors.textSecondary, fontWeight: 500 }}
            >
              No actions for you right now
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.gray[400] }}
            >
              Waiting on others to complete their tasks
            </Typography>
          </Paper>
        ) : null}

        {/* Waiting on Others Card */}
        {waitingOnOthers.length > 0 && (
          <WaitingOnOthersCard
            tasks={waitingOnOthers}
            participants={participants}
            roles={roles}
            onNavigate={onNavigateToTask}
            totalCount={steps.filter((s) => {
              if (s.status === StepStatus.Completed || s.status === StepStatus.Skipped) return false;
              const resolved = resolveStepAssignee(s, roles, participants);
              return resolved.participant && resolved.participant.id !== currentParticipantId;
            }).length}
          />
        )}

        {/* Empty state */}
        {taskTree.rootTasks.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: colors.textSecondary,
            }}
          >
            <Typography variant="body1">No tasks in this plan yet.</Typography>
          </Box>
        )}
      </Box>

      {/* Roles sidebar */}
      <Box sx={{ flex: 1, minWidth: { xs: 'auto', md: 280 }, maxWidth: { md: 320 } }}>
        <ParticipantsPanel
          planId={planId}
          roles={roles}
          participants={participants}
          steps={steps}
          currentParticipantId={currentParticipantId}
        />
      </Box>
    </Box>
  );
};

// Waiting on Others card component
interface WaitingOnOthersCardProps {
  tasks: PlanStep[];
  participants: Participant[];
  roles: PlanRole[];
  onNavigate: (taskId: string) => void;
  totalCount: number;
}

const WaitingOnOthersCard: React.FC<WaitingOnOthersCardProps> = ({
  tasks,
  participants,
  roles,
  onNavigate,
  totalCount,
}) => {
  const getResolvedAssignee = (step: PlanStep) => {
    return resolveStepAssignee(step, roles, participants);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          bgcolor: colors.amber[50],
          borderBottom: `1px solid ${colors.gray[200]}`,
        }}
      >
        <HourglassEmptyIcon sx={{ fontSize: 18, color: colors.amber[600] }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: colors.textPrimary }}
        >
          Waiting on others
        </Typography>
        <Chip
          label={totalCount}
          size="small"
          sx={{
            ml: 'auto',
            bgcolor: 'white',
            height: 22,
            '& .MuiChip-label': {
              px: 1,
              fontSize: '0.75rem',
              color: colors.textSecondary,
            },
          }}
        />
      </Box>
      <Stack divider={<Box sx={{ borderBottom: `1px solid ${colors.gray[100]}` }} />}>
        {tasks.map((task) => {
          const resolved = getResolvedAssignee(task);
          const owner = resolved.participant;
          const role = resolved.role;
          return (
            <Box
              key={task.id}
              onClick={() => onNavigate(task.id)}
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                cursor: 'pointer',
                transition: 'background-color 0.15s',
                '&:hover': {
                  bgcolor: colors.gray[50],
                },
              }}
            >
              {owner && (
                <GravatarAvatar
                  email={owner.email}
                  name={owner.name}
                  size={24}
                  showTooltip={false}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.textPrimary,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {task.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.textSecondary }}
                >
                  {role && <span style={{ marginRight: 4 }}>{role.name}</span>}
                  {owner ? (
                    <>• {owner.name}</>
                  ) : role ? (
                    <span style={{ color: colors.amber[600] }}>• Not assigned</span>
                  ) : null}
                </Typography>
              </Box>
              <Chip
                label="Waiting"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: colors.amber[50],
                  color: colors.amber[700],
                  border: `1px solid ${colors.amber[200]}`,
                }}
              />
            </Box>
          );
        })}
        {totalCount > 3 && (
          <Box
            sx={{
              px: 2,
              py: 1,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.blue[600], fontWeight: 500, cursor: 'pointer' }}
            >
              View all {totalCount} items
            </Typography>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default OverviewPlaybookSpace;
